import type { PublicProfile } from "@pax/api-contract/common";
import { cn, focusHaloFlush, panelMotion, Skeleton } from "@pax/ui";
import { type UseInfiniteQueryResult, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, type LinkProps } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { type Ref, useState } from "react";
import { AssetGrid } from "@/features/assets/components/AssetGrid";
import { CollectionGrid } from "@/features/assets/components/CollectionCard";
import { useSession } from "@/features/auth/queries";
import { Earnings, type EarningsSearch } from "@/features/earnings/EarningsScreen";
import { useHasEarnings } from "@/features/earnings/queries";
import { myPresetsQuery } from "@/features/preset-editor/queries";
import { PresetCard } from "@/features/presets/components/PresetCard";
import { profileAssetsQuery, profileCollectionsQuery, profilePresetsQuery } from "../queries";

export type Shelf = "presets" | "playlists" | "assets" | "collections" | "earnings";

/** A tab's count, or `soon` for one that isn't built yet. */
type ShelfCount = number | `${number}+` | "soon";

/**
 * What they've made, a tab each, each with its count. A tab with nothing in it isn't shown, and
 * the row scrolls sideways when the tabs don't fit. On your own profile, Presets and Assets always
 * show, each with a link to everything of yours including drafts, and Earnings shows once you've
 * earned.
 */
export function ProfileShelves({
  profile,
  own,
  shelf,
  onShelfChange,
  ref,
}: {
  profile: PublicProfile;
  own: boolean;
  /** The tab asked for, which falls back to the first one shown when it has nothing in it. */
  shelf: Shelf;
  onShelfChange: (shelf: Shelf) => void;
  ref?: Ref<HTMLDivElement>;
}) {
  const assets = useInfiniteQuery(profileAssetsQuery(profile.profileId, own));
  const collections = useInfiniteQuery(profileCollectionsQuery(profile.profileId));
  const assetList = assets.data?.pages.flatMap(page => page.assets);
  const collectionList = collections.data?.pages.flatMap(page => page.collections);
  const { data: session } = useSession();
  const hasEarnings = useHasEarnings(own ? session?.user : undefined);
  const [earnings, setEarnings] = useState<EarningsSearch>({});

  const assetCount = assetList && pageCount(assetList.length, assets.hasNextPage);
  const tabs: { id: Shelf; label: string; count?: ShelfCount; shown: boolean }[] = [
    {
      id: "presets",
      label: "Presets",
      count: profile.publishedPresetCount,
      shown: own || profile.publishedPresetCount > 0,
    },
    { id: "playlists", label: "Playlists", count: "soon", shown: true },
    {
      id: "assets",
      label: "Assets",
      count: assetCount,
      shown: own || (assetCount !== undefined && assetCount !== 0),
    },
    {
      id: "collections",
      label: "Collections",
      count: collectionList && pageCount(collectionList.length, collections.hasNextPage),
      shown: (collectionList?.length ?? 0) > 0,
    },
    { id: "earnings", label: "Earnings", shown: own && hasEarnings },
  ];
  const shown = tabs.filter(tab => tab.shown);
  const selected = shown.some(tab => tab.id === shelf) ? shelf : shown[0].id;

  return (
    <div ref={ref} className="scroll-mt-4">
      <div
        role="tablist"
        aria-label="What they've made"
        className="-mx-6 mb-4 flex gap-1 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {shown.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === selected}
            onClick={() => onShelfChange(tab.id)}
            className={cn(
              "flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium whitespace-nowrap",
              tab.id === selected
                ? "bg-foreground/[0.07] text-foreground"
                : "text-muted-foreground hover:text-foreground",
              panelMotion,
              focusHaloFlush,
            )}
          >
            {tab.label}
            {tab.count === "soon" ? (
              <span className="rounded-full bg-foreground/10 px-1.5 py-px text-[10px] font-medium tracking-wide uppercase opacity-70">
                Soon
              </span>
            ) : tab.count !== undefined ? (
              <span className="text-xs tabular-nums opacity-60">{tab.count.toLocaleString()}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {selected === "presets" ? (
          <>
            {own ? <MyPresetsLink /> : null}
            <PresetShelf profileId={profile.profileId} />
          </>
        ) : null}
        {selected === "playlists" ? (
          <p className="py-16 text-center text-sm text-muted-foreground">Coming soon</p>
        ) : null}
        {selected === "assets" ? (
          <>
            {own ? <MineLink to="/assets/mine" label="My assets" /> : null}
            {assetList ? <AssetGrid assets={assetList} /> : null}
            <ShowMore query={assets} />
          </>
        ) : null}
        {selected === "collections" && collectionList ? (
          <>
            <CollectionGrid collections={collectionList} />
            <ShowMore query={collections} />
          </>
        ) : null}
        {selected === "earnings" ? (
          <Earnings search={earnings} onSearchChange={setEarnings} />
        ) : null}
      </div>
    </div>
  );
}

/** How many have loaded, with a plus while there are more pages to load. */
function pageCount(length: number, more: boolean): ShelfCount {
  return more ? `${length}+` : length;
}

/** Loads a shelf's next page, while it has one. */
function ShowMore({ query }: { query: UseInfiniteQueryResult }) {
  if (!query.hasNextPage) return null;
  return (
    <button
      type="button"
      disabled={query.isFetchingNextPage}
      onClick={() => void query.fetchNextPage()}
      className={cn(
        "mt-4 flex h-11 w-full items-center justify-center rounded-2xl bg-foreground/[0.04] text-sm font-medium text-muted-foreground hover:bg-foreground/[0.07] disabled:opacity-50",
        panelMotion,
        focusHaloFlush,
      )}
    >
      {query.isFetchingNextPage ? "Loading…" : "Show more"}
    </button>
  );
}

function PresetShelf({ profileId }: { profileId: string }) {
  const query = useInfiniteQuery(profilePresetsQuery(profileId));
  const presets = query.data?.pages.flatMap(page => page.items);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {presets === undefined
          ? ["a", "b", "c"].map(placeholder => (
              <Skeleton key={placeholder} className="aspect-[16/10] w-full rounded-2xl" />
            ))
          : presets.map(listing => <PresetCard key={listing.slug} listing={listing} />)}
      </div>
      <ShowMore query={query} />
    </>
  );
}

/** Your presets with drafts, team presets, and archived ones, and how many invitations wait. */
function MyPresetsLink() {
  const { data: mine } = useQuery(myPresetsQuery());
  const invitations = mine?.invitations.length ?? 0;
  return (
    <MineLink
      to="/presets/mine"
      label="My presets"
      detail={
        invitations > 0
          ? `${invitations} ${invitations === 1 ? "invitation" : "invitations"}`
          : undefined
      }
    />
  );
}

/** A row above your own shelf, to the page with everything of yours, drafts included. */
function MineLink({ to, label, detail }: { to: LinkProps["to"]; label: string; detail?: string }) {
  return (
    <Link
      to={to}
      className={cn(
        "mb-4 flex h-11 items-center gap-2 rounded-2xl bg-foreground/[0.04] px-4 text-sm font-medium hover:bg-foreground/[0.07]",
        panelMotion,
        focusHaloFlush,
      )}
    >
      {label}
      <span className="ml-auto text-xs text-muted-foreground tabular-nums">{detail}</span>
      <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
    </Link>
  );
}
